import React, { useState } from 'react';
import { C } from '../styles/common';

// The orders as they were at a point, under the board (the director, 6 Oct
// 2026: "since we are reverting both, we should show it below as well").
//
// Driven by the page's one switch -- as it was / as it is now -- and in the
// board legend's colours: amber changed since (the other value in small type
// beside it), teal added since, red dashed taken off since. By default only
// the orders that changed since are shown; every order is one click away.

const TEAL = C.teal, AMBER = '#f59e0b', RED = '#f87171', DIM = '#9ca3af';

const day = (s) => new Date(String(s).slice(0, 10) + 'T12:00:00Z')
  .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const qty = (v) => (v == null ? '—' : Number(v).toLocaleString('en-GB'));
const delivery = (h) => (h.delivery_date ? day(h.delivery_date) : h.delivery_week ? `week ${h.delivery_week}` : 'none');
const status = (h) => (h.status === 'fulfilled' ? 'Completed' : h.status === 'open' ? 'Open' : h.status || '—');
const sortKey = (o) => {
  const h = o.now || o.then || {};
  return `${h.delivery_date || ''}|${String(h.delivery_year || 0).padStart(4, '0')}|${String(h.delivery_week || 0).padStart(2, '0')}|${h.order_number || ''}`;
};

// What happened to a line's run on the board since, said in the board's
// colours (the director, 6 Oct: a changed job should show on its order line).
const shortDay = (s) => new Date(String(s).slice(0, 10) + 'T12:00:00Z')
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const span = (x) => (x.from === x.to ? shortDay(x.from) : `${shortDay(x.from)}–${shortDay(x.to)}`);
function runTag(c, mach) {
  if (c.kind === 'moved') {
    const where = c.then.machine_id === c.now.machine_id ? `${mach(c.now.machine_id)} ` : '';
    return { text: 'run moved since', tone: AMBER,
      detail: where + span(c.then) + (where ? '' : ` on ${mach(c.then.machine_id)}`) + ' → ' + span(c.now) + (where ? '' : ` on ${mach(c.now.machine_id)}`) };
  }
  if (c.kind === 'added') return { text: 'run put on since', tone: TEAL, detail: `${mach(c.now.machine_id)} ${span(c.now)}` };
  if (c.kind === 'removed') return { text: 'run taken off since', tone: RED, detail: `was ${mach(c.then.machine_id)} ${span(c.then)}` };
  return { text: 'run quantity changed', tone: AMBER, detail: `${qty(c.then.qty)} → ${qty(c.now.qty)} pcs` };
}

const SORT_KEY = 'mouldos.ordersAtPoint.latestFirst';

export default function OrdersAtPoint({ orders = [], parts = {}, view = 'then', changes = [], mach = (id) => `machine ${id}` }) {
  const [showAll, setShowAll] = useState(false);
  // Earliest or latest delivery first (the director, 6 Oct); remembered on
  // this computer only, and the page works the same if it cannot be.
  const [latestFirst, setLatestFirst] = useState(() => {
    try { return localStorage.getItem(SORT_KEY) === '1'; } catch { return false; }
  });
  const chooseSort = (v) => {
    setLatestFirst(v);
    try { localStorage.setItem(SORT_KEY, v ? '1' : '0'); } catch { /* not kept, still sorted */ }
  };
  // Board changes by the order line their run is for.
  const runsByLine = new Map();
  for (const c of changes) for (const l of c.lines || []) {
    if (!runsByLine.has(l)) runsByLine.set(l, []);
    runsByLine.get(l).push(c);
  }
  const runsChanged = (o) => [...((o.then && o.then.lines) || []), ...((o.now && o.now.lines) || [])].some(l => runsByLine.has(l.id));
  const changed = orders.filter(o => o.kind !== 'same' || runsChanged(o));
  const side = view === 'then' ? 'then' : 'now';
  const shown = (showAll ? orders : changed).slice()
    .sort((a, b) => (latestFirst ? -1 : 1) * sortKey(a).localeCompare(sortKey(b)));
  const here = shown.filter(o => o[side]);
  // Orders that are on one side only, said in one quiet line each.
  const absent = shown.filter(o => !o[side]);

  return (
    <div id="orders-at-point" style={{ marginTop: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em' }}>
          {view === 'then' ? 'Orders as they were' : 'Orders as they are now'}
        </div>
        {/* What to show, then in which order. The order is one choice for
            both lists, and appears only when there are two or more orders
            to put in order -- sorting one means nothing (the director, 6 Oct). */}
        <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 7, overflow: 'hidden', marginLeft: 'auto' }}>
          {[[false, `Only what changed (${changed.length})`], [true, `All orders (${orders.length})`]].map(([v, label], i) => (
            <button key={label} onClick={() => setShowAll(v)} aria-pressed={showAll === v}
              style={{ background: showAll === v ? C.tealDim : 'none', color: showAll === v ? TEAL : DIM, border: 'none',
                       borderRight: i === 0 ? `1px solid ${C.border}` : 'none', padding: '5px 12px', fontSize: 12.5,
                       fontWeight: showAll === v ? 700 : 500, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
        {shown.length > 1 && (
          <button onClick={() => chooseSort(!latestFirst)}
            title={latestFirst ? 'Show the earliest delivery first' : 'Show the latest delivery first'}
            style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 7, color: DIM, padding: '5px 12px',
                     fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <i className={`fa-solid ${latestFirst ? 'fa-arrow-down-wide-short' : 'fa-arrow-up-short-wide'}`}
               style={{ fontSize: 11, marginRight: 6, color: TEAL }} />
            {latestFirst ? 'Latest delivery first' : 'Earliest delivery first'}
          </button>
        )}
      </div>

      {!showAll && changed.length === 0 && (
        <div style={{ fontSize: 13, color: C.muted }}>No order has changed since then.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {here.map(o => <OrderCard key={o.id} o={o} side={side} parts={parts}
                                  runsByLine={runsByLine} runsChanged={runsChanged(o)} mach={mach} />)}
      </div>

      {absent.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
          {absent.map(o => (
            <div key={o.id} style={{ fontSize: 12.5, color: DIM, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                             border: `1.5px ${o.kind === 'taken_since' ? 'solid' : 'dashed'} ${o.kind === 'taken_since' ? TEAL : RED}` }} />
              <b style={{ color: '#d1d5db' }}>{o.number}</b>
              {o.kind === 'taken_since' ? '— taken after this point, so it was not here then.' : '— deleted since, so it is not here now.'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard({ o, side, parts, runsByLine, runsChanged, mach }) {
  const mine = o[side];
  const other = side === 'then' ? o.now : o.then;
  const otherWord = side === 'then' ? 'now' : 'then';
  const badge = o.kind === 'changed' || o.kind === 'lines_unknown' ? { text: 'Changed since', tone: AMBER }
    : o.kind === 'taken_since' ? { text: 'Taken since', tone: TEAL }
    : o.kind === 'deleted_since' ? { text: 'Deleted since', tone: RED }
    : runsChanged ? { text: 'Runs changed since', tone: AMBER } : null;

  // One header value, with the other side beside it when they differ.
  const Field = ({ label, get }) => {
    const a = get(mine), b = other ? get(other) : a;
    const moved = other && a !== b;
    return (
      <span style={{ display: 'inline-flex', gap: 5, alignItems: 'baseline' }}>
        <span style={{ color: C.muted, fontSize: 11.5 }}>{label}</span>
        <span style={{ color: moved ? AMBER : '#e5e7eb', fontWeight: moved ? 700 : 500 }}>{a}</span>
        {moved && <span style={{ color: DIM, fontSize: 11 }}>({otherWord}: {b})</span>}
      </span>
    );
  };
  const nameOf = (h) => [h.order_number, h.customer].filter(Boolean).join(' · ') || `Order ${o.id}`;
  const nameMoved = other && nameOf(mine) !== nameOf(other);

  const otherLines = new Map(((other && other.lines) || []).map(l => [l.id, l]));
  const mineIds = new Set((mine.lines || []).map(l => l.id));
  const onlyOther = other && other.lines && mine.lines ? other.lines.filter(l => !mineIds.has(l.id)) : [];

  return (
    <div id={`order-${o.id}`} style={{ border: `1px solid ${badge ? `${badge.tone}55` : C.border}`, borderRadius: 10, background: C.surface, overflow: 'hidden', scrollMarginTop: 80 }}>
      <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '6px 16px', alignItems: 'baseline', fontSize: 13 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: nameMoved ? AMBER : '#f0f2f5' }}>
          {nameOf(mine)}
          {nameMoved && <span style={{ color: DIM, fontSize: 11, fontWeight: 400, marginLeft: 6 }}>({otherWord}: {nameOf(other)})</span>}
        </span>
        <Field label="Delivery" get={delivery} />
        {(mine.dispatch_date || (other && other.dispatch_date)) && <Field label="Dispatch" get={h => (h.dispatch_date ? day(h.dispatch_date) : 'none')} />}
        <Field label="Status" get={status} />
        {badge && (
          <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                         color: badge.tone, background: `${badge.tone}1f`, border: `1px solid ${badge.tone}66`, borderRadius: 5, padding: '2px 8px' }}>
            {badge.text}
          </span>
        )}
      </div>

      {mine.lines === null ? (
        <div style={{ padding: '9px 14px 12px', borderTop: `1px solid ${C.border}`, fontSize: 12.5, color: DIM, lineHeight: 1.55 }}>
          <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />
          Its lines as they were then were not kept: before 6 Oct the app saved an order's lines only after a change.
          Reverting puts its details above back and leaves its lines as they are now.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderTop: `1px solid ${C.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr style={{ background: C.surfaceAlt, color: C.muted, textAlign: 'left' }}>
                {['Part number', 'Part', 'Quantity', 'From stock', ''].map(h => (
                  <th key={h} style={{ padding: '6px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mine.lines.map(l => {
                const was = otherLines.get(l.id);
                const onlyHere = other && other.lines && !was;
                const tag = onlyHere ? (side === 'then' ? { text: 'taken off since', tone: RED, dashed: true } : { text: 'added since', tone: TEAL }) : null;
                const runs = (runsByLine.get(l.id) || []).map(c => runTag(c, mach));
                const mark = tag || runs[0] || null;
                const cell = (get, fmt) => {
                  const a = get(l), b = was ? get(was) : a;
                  const moved = was && a !== b;
                  return (
                    <span>
                      <span style={{ color: moved ? AMBER : undefined, fontWeight: moved ? 700 : undefined }}>{fmt(a)}</span>
                      {moved && <span style={{ color: DIM, fontSize: 11, marginLeft: 5 }}>({otherWord}: {fmt(b)})</span>}
                    </span>
                  );
                };
                return (
                  <tr key={l.id} style={{ borderTop: `1px solid ${C.border}`,
                                          boxShadow: mark ? `inset 3px 0 0 ${mark.tone}` : 'none',
                                          background: !tag && runs.length ? `${runs[0].tone}14` : 'transparent',
                                          opacity: tag && tag.dashed ? 0.85 : 1 }}>
                    <td style={{ padding: '6px 14px', color: DIM, whiteSpace: 'nowrap' }}>{cell(x => x.part_number, v => v || '—')}</td>
                    <td style={{ padding: '6px 14px' }}><b>{cell(x => parts[x.part_id] || `Part ${x.part_id}`, v => v)}</b></td>
                    <td style={{ padding: '6px 14px' }}>{cell(x => x.quantity, qty)}</td>
                    <td style={{ padding: '6px 14px' }}>{cell(x => x.from_stock_qty, qty)}</td>
                    <td style={{ padding: '6px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                        {tag && (
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: tag.tone, border: `1px ${tag.dashed ? 'dashed' : 'solid'} ${tag.tone}88`, borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                            {tag.text}
                          </span>
                        )}
                        {runs.map((r, i) => (
                          <span key={i} style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: r.tone, border: `1px solid ${r.tone}88`, borderRadius: 4, padding: '1px 6px', marginRight: 6 }}>
                              {r.text}
                            </span>
                            <span style={{ color: '#d1d5db' }}>{r.detail}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {onlyOther.length > 0 && (
            <div style={{ padding: '7px 14px 10px', fontSize: 12, color: DIM, borderTop: `1px solid ${C.border}` }}>
              {side === 'then' ? 'Added since: ' : 'Taken off since: '}
              {onlyOther.map(l => parts[l.part_id] || l.part_number || `line ${l.id}`).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
